import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CloudOff, Trash2, MapPin, Mountain, HardDrive, WifiOff, Package } from "lucide-react";
import { toast } from "sonner";
import { listGuides, removeGuide, storageEstimate, offlineSupported } from "../lib/offline";

function fmtBytes(n) {
    if (!n) return "0 MB";
    const mb = n / (1024 * 1024);
    if (mb < 1024) return `${mb.toFixed(mb < 10 ? 1 : 0)} MB`;
    return `${(mb / 1024).toFixed(1)} GB`;
}

/**
 * "My Offline Guides" shelf — lists every adventure the user explicitly saved
 * for offline use. Works with zero signal because everything (detail JSON,
 * photos, GPX) was pre-cached by lib/offline.saveGuide.
 */
export default function OfflineGuidesShelf() {
    const [guides, setGuides] = useState([]);
    const [usage, setUsage] = useState(null);
    const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);

    const refresh = () => setGuides(listGuides());

    useEffect(() => {
        refresh();
        storageEstimate().then(setUsage);
        const on = () => setOnline(true);
        const off = () => setOnline(false);
        window.addEventListener("online", on);
        window.addEventListener("offline", off);
        return () => {
            window.removeEventListener("online", on);
            window.removeEventListener("offline", off);
        };
    }, []);

    const onRemove = async (id) => {
        await removeGuide(id);
        refresh();
        storageEstimate().then(setUsage);
        toast.success("Removed from offline guides");
    };

    if (!offlineSupported()) {
        return (
            <div data-testid="offline-unsupported" className="rounded-2xl border border-ridge-line bg-snowcap p-8 text-center">
                <CloudOff className="w-10 h-10 text-slate-gray mx-auto mb-3" />
                <p className="text-sm text-slate-gray">Offline saving isn't supported on this browser.</p>
            </div>
        );
    }

    if (guides.length === 0) {
        return (
            <div data-testid="offline-empty" className="rounded-2xl border border-dashed border-ridge-line bg-snowcap p-10 text-center">
                <span className="w-14 h-14 rounded-2xl bg-alpine-mist flex items-center justify-center mx-auto mb-4">
                    <CloudOff className="w-7 h-7 text-slate-gray" />
                </span>
                <h3 className="text-lg font-bold text-midnight-pine mb-1">No offline guides yet</h3>
                <p className="text-sm text-slate-gray max-w-sm mx-auto mb-4">
                    On any adventure, tap <span className="font-bold text-granite">Save offline</span> to pre-load its
                    photos and route. Saved guides open even with zero signal at the trailhead.
                </p>
                <Link
                    to="/explore"
                    data-testid="offline-browse-link"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-midnight-pine text-white text-sm font-bold hover:bg-deep-evergreen transition-all"
                >
                    <Mountain className="w-4 h-4" /> Find adventures to save
                </Link>
            </div>
        );
    }

    return (
        <div data-testid="offline-shelf" className="space-y-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 text-xs text-slate-gray">
                    <HardDrive className="w-4 h-4" />
                    <span>
                        {guides.length} {guides.length === 1 ? "guide" : "guides"} on this device
                        {usage ? ` · ${fmtBytes(usage.usage)} used` : ""}
                    </span>
                </div>
                {!online && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-ember-orange/10 text-ember-orange text-xs font-bold">
                        <WifiOff className="w-3.5 h-3.5" /> Offline — showing saved guides
                    </span>
                )}
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
                {guides.map((g) => (
                    <div
                        key={g.id}
                        data-testid={`offline-guide-${g.id}`}
                        className="group rounded-2xl border border-ridge-line bg-snowcap overflow-hidden flex flex-col"
                    >
                        <Link to={`/adventures/${g.id}`} className="block relative aspect-[16/9] bg-trail-tan overflow-hidden">
                            {g.cover ? (
                                <img src={g.cover} alt={g.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                    <Mountain className="w-8 h-8 text-midnight-pine/40" />
                                </div>
                            )}
                            <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-midnight-pine/85 text-white text-[10px] font-bold uppercase tracking-wide">
                                <CloudOff className="w-3 h-3" /> Offline ready
                            </span>
                        </Link>
                        <div className="p-4 flex-1 flex flex-col">
                            <Link to={`/adventures/${g.id}`}>
                                <h3 className="font-bold text-midnight-pine leading-tight line-clamp-2 hover:text-ember-orange transition-colors">
                                    {g.title}
                                </h3>
                            </Link>
                            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-gray">
                                {g.activity && <span className="capitalize">{g.activity}</span>}
                                {g.difficulty && <span className="capitalize">· {g.difficulty}</span>}
                                {g.location && (
                                    <span className="inline-flex items-center gap-1 min-w-0">
                                        <MapPin className="w-3 h-3 shrink-0" />
                                        <span className="truncate">{g.location}</span>
                                    </span>
                                )}
                            </div>
                            <div className="mt-4 pt-3 border-t border-ridge-line flex items-center justify-between gap-2">
                                <Link
                                    to={`/adventures/${g.id}`}
                                    data-testid={`offline-open-${g.id}`}
                                    className="text-sm font-bold text-midnight-pine hover:text-ember-orange transition-colors"
                                >
                                    Open guide
                                </Link>
                                <div className="flex items-center gap-2">
                                    <a
                                        href={`${process.env.REACT_APP_BACKEND_URL}/api/adventures/${g.id}/trip-pack.zip`}
                                        data-testid={`offline-pack-${g.id}`}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-midnight-pine text-white text-xs font-bold hover:bg-deep-evergreen transition-all"
                                    >
                                        <Package className="w-3.5 h-3.5" /> Pack
                                    </a>
                                    <button
                                        data-testid={`offline-remove-${g.id}`}
                                        onClick={() => onRemove(g.id)}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-ridge-line text-slate-gray text-xs font-bold hover:border-trail-red hover:text-trail-red transition-all"
                                    >
                                        <Trash2 className="w-3.5 h-3.5" /> Remove
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
